import { useState, useEffect } from 'react';
import { BarChart3, Heart, Download, TrendingUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { API, authFetch, exportCSV } from './helpers';
import { getAffirmationOfTheDay } from '@/data/affirmations';

const CHART_COLORS = ['#C49D4D', '#7C6930', '#3B3520', '#D4B36A', '#A88740'];

const DashboardTab = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [regChart, setRegChart] = useState([]);
  const [contactChart, setContactChart] = useState({ monthly: [], by_status: [] });
  const [ticketChart, setTicketChart] = useState({ monthly_revenue: [], by_type: [] });
  const [dailyAffirmation] = useState(getAffirmationOfTheDay());

  useEffect(() => {
    const fetchAll = async () => {
      const [statsRes, regRes, contactRes, ticketRes] = await Promise.all([
        authFetch(`${API}/stats`),
        authFetch(`${API}/analytics/registrations`),
        authFetch(`${API}/analytics/contacts`),
        authFetch(`${API}/analytics/tickets`),
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (regRes.ok) setRegChart(await regRes.json());
      if (contactRes.ok) setContactChart(await contactRes.json());
      if (ticketRes.ok) setTicketChart(await ticketRes.json());
    };
    fetchAll();
  }, []);

  if (!stats) return <p className="text-gray-500">Loading stats...</p>;

  const hasChartData = regChart.length > 0 || contactChart.monthly.length > 0 || ticketChart.monthly_revenue.length > 0;

  return (
    <div>
      <div className="bg-brand-dark p-6 mb-8" data-testid="affirmation-card">
        <div className="flex items-start gap-4">
          <Heart className="w-6 h-6 text-brand-gold flex-shrink-0 mt-1" />
          <div>
            <p className="text-sm text-brand-gold font-medium mb-1">Today's Affirmation</p>
            <p className="text-white text-lg italic">"{dailyAffirmation}"</p>
          </div>
        </div>
      </div>

      <h2 className="text-xl font-semibold text-brand-dark mb-6">Overview</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8" data-testid="stats-grid">
        {[
          { label: 'Total Contacts', value: stats.total_contacts },
          { label: 'New Contacts', value: stats.new_contacts },
          { label: 'Event Registrations', value: stats.total_registrations },
          { label: 'Expected Attendees', value: stats.total_attendees },
          { label: 'Newsletter Subs', value: stats.newsletter_subscribers },
          { label: 'Published Posts', value: stats.newsletter_posts || 0 },
          { label: 'Team Members', value: stats.team_members },
        ].map((s) => (
          <div key={s.label} className="bg-white p-6">
            <p className="text-3xl font-semibold text-brand-gold">{s.value}</p>
            <p className="text-sm text-gray-600">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Analytics Charts */}
      {hasChartData && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-6">
            <TrendingUp className="w-5 h-5 text-brand-gold" />
            <h3 className="text-lg font-semibold text-brand-dark">Analytics</h3>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="analytics-charts">
            {/* Registrations Over Time */}
            {regChart.length > 0 && (
              <div className="bg-white p-6">
                <h4 className="text-sm font-medium text-gray-600 mb-4">Registrations by Month</h4>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={regChart}>
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#888' }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#888' }} />
                    <Tooltip contentStyle={{ fontSize: 12, border: '1px solid #eee' }} />
                    <Bar dataKey="count" fill="#C49D4D" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Contacts by Status */}
            {contactChart.by_status.length > 0 && (
              <div className="bg-white p-6">
                <h4 className="text-sm font-medium text-gray-600 mb-4">Contacts by Status</h4>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={contactChart.by_status} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={80} label={({ status, count }) => `${status} (${count})`} labelLine={false}>
                      {contactChart.by_status.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Ticket Revenue */}
            {ticketChart.monthly_revenue.length > 0 && (
              <div className="bg-white p-6">
                <h4 className="text-sm font-medium text-gray-600 mb-4">Ticket Revenue by Month</h4>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={ticketChart.monthly_revenue}>
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#888' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#888' }} tickFormatter={(v) => `$${v}`} />
                    <Tooltip formatter={(v) => [`$${v.toFixed(2)}`, 'Revenue']} contentStyle={{ fontSize: 12 }} />
                    <Bar dataKey="revenue" fill="#3B3520" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Ticket Sales by Type */}
            {ticketChart.by_type.length > 0 && (
              <div className="bg-white p-6">
                <h4 className="text-sm font-medium text-gray-600 mb-4">Tickets Sold by Type</h4>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={ticketChart.by_type} dataKey="sold" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, sold }) => `${name} (${sold})`} labelLine={false}>
                      {ticketChart.by_type.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      )}

      {(user?.role === 'admin' || user?.role === 'staff') && (
        <>
          <h3 className="text-lg font-semibold text-brand-dark mb-4">Quick Export</h3>
          <div className="flex flex-wrap gap-4">
            {['contacts', 'registrations', 'newsletter'].map((type) => (
              <button
                key={type}
                onClick={() => exportCSV(type)}
                className="flex items-center gap-2 bg-white border border-gray-300 px-4 py-2 text-sm font-medium text-brand-dark hover:bg-gray-50"
                data-testid={`export-${type}-btn`}
              >
                <Download className="w-4 h-4" />
                Export {type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default DashboardTab;
